import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiArrowRight, FiSend, FiSave, FiCheckCircle } from 'react-icons/fi';
import { useAppContext } from '@/context/AppContext';
import ncbClient from '@/utils/ncbClient';
import { promptCompiler } from '@/utils/promptCompiler';
import logger from '@/utils/logger';
import Step1Identity from '@/components/wizard/Step1Identity';
import Step2Configuration from '@/components/wizard/Step2Configuration';
import Step3Integrations from '@/components/wizard/Step3Integrations';
import Step4FeaturesPages from '@/components/wizard/Step4FeaturesPages';
import Step5BusinessSEO from '@/components/wizard/Step5BusinessSEO';
import PromptModal from '@/components/PromptModal';


const steps = [
  { name: 'Identity', component: Step1Identity },
  { name: 'Configuration', component: Step2Configuration },
  { name: 'Integrations', component: Step3Integrations },
  { name: 'Features & Pages', component: Step4FeaturesPages },
  { name: 'Business & SEO', component: Step5BusinessSEO },
];

function StartBuilding() {
  const [currentStep, setCurrentStep] = useState(0);
  const [projectId, setProjectId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(''); // '', 'success', 'error'
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState('');

  const navigate = useNavigate();
  const { projectDraft } = useAppContext();

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveProject = async () => {
    setIsSaving(true);
    setSaveStatus('');
    try {
      let currentProjectId = projectId;
      if (currentProjectId) {
        await ncbClient.updateProject(currentProjectId, { projectDraft: JSON.stringify(projectDraft) });
        logger.success('Project updated successfully.', { projectId: currentProjectId });
      } else {
        const newProject = await ncbClient.createProject(projectDraft);
        setProjectId(newProject.id);
        currentProjectId = newProject.id;
        logger.success('Project saved successfully.', { projectId: currentProjectId });
      }
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(''), 2000);
      return currentProjectId;
    } catch (error) {
      logger.error("Failed to save project", { error: error.message });
      setSaveStatus('error');
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const handleGeneratePrompt = () => {
    try {
        const prompt = promptCompiler(projectDraft);
        setGeneratedPrompt(prompt);
        setIsPromptModalOpen(true);
    } catch(error) {
        logger.error("Prompt Generation Failed", {error: error.message});
    }
  };
  
  const handleSavePromptToProject = async () => {
    let currentProjectId = projectId;
    if (!currentProjectId) {
        currentProjectId = await handleSaveProject();
    }
    
    if (currentProjectId) {
        try {
            await ncbClient.updateProject(currentProjectId, {
                generatedPrompt: generatedPrompt,
                projectDraft: JSON.stringify(projectDraft),
            });
            logger.success('Prompt successfully saved to project.', { projectId: currentProjectId });
            setIsPromptModalOpen(false);
        } catch (error) {
            logger.error('Failed to save prompt to project.', { error: error.message });
        }
    }
  };

  const CurrentStepComponent = steps[currentStep].component;

  return (
    <div className="max-w-4xl mx-auto">
        <PromptModal
            isOpen={isPromptModalOpen}
            onClose={() => setIsPromptModalOpen(false)}
            promptText={generatedPrompt}
            onSave={handleSavePromptToProject}
        />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Start Building Your Project</h1>
        <p className="text-gray-500 mt-1">Follow the steps to configure your new application.</p>
      </div>
      <div className="mb-8">
        <div className="bg-gray-200 rounded-full h-2.5">
          <motion.div
            className="bg-indigo-600 h-2.5 rounded-full"
            animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            transition={{ ease: "easeInOut", duration: 0.5 }}
          />
        </div>
      </div>
      <div className="bg-white p-8 rounded-xl shadow-lg">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ x: 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -30, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <CurrentStepComponent />
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-8 flex justify-between items-center">
        <button
          onClick={handleBack}
          disabled={currentStep === 0}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiArrowLeft className="mr-2 h-5 w-5" />
          Back
        </button>
        <div className="flex items-center space-x-4">
          <button
            onClick={handleSaveProject}
            disabled={isSaving}
            className="inline-flex items-center px-4 py-2 border border-blue-500 text-sm font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : (saveStatus === 'success' ? <><FiCheckCircle className="mr-2"/> Saved!</> : <><FiSave className="mr-2 h-5 w-5" /> {projectId ? 'Update Project' : 'Save Project'}</>)}
          </button>
          
          {currentStep < steps.length - 1 ? (
            <button
              onClick={handleNext}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Next
              <FiArrowRight className="ml-2 h-5 w-5" />
            </button>
          ) : (
            <button
              onClick={handleGeneratePrompt}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
            >
              Generate Prompt
              <FiSend className="ml-2 h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default StartBuilding;